import { motion } from "framer-motion";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { BRAND } from "@/constants/compliance";

const FAQS = [
  {
    q: "How do I sign up for reminders?",
    a: `Two easy ways: text the keyword ${BRAND.keyword} to ${BRAND.number}, or fill out the sign-up form on this page and check the consent box. Either way, you'll get an instant welcome message confirming you're in.`,
  },
  {
    q: "How many messages will I receive?",
    a: "Message frequency varies — it depends entirely on how many tasks and reminders you schedule. A light week might mean one or two texts; a busy one, a few more. We never send marketing blasts, only your reminders and service updates.",
  },
  {
    q: "Will I be charged for these texts?",
    a: "We don't charge you anything to receive reminders, but message & data rates may apply depending on your mobile plan. If you're unsure, check with your wireless carrier.",
  },
  {
    q: "How do I stop the messages?",
    a: `Text the single word STOP to ${BRAND.number} at any time. You'll get one final confirmation text, and then silence — no follow-ups, no "are you sure?" messages. Changed your mind later? Just text ${BRAND.keyword} again to rejoin.`,
  },
  {
    q: "What if I need help?",
    a: `Reply HELP to any message from us (or text HELP to ${BRAND.number}) and we'll send support information and your options. You can also email us at ${BRAND.email} — a human reads every message.`,
  },
  {
    q: "Do you share my phone number with anyone?",
    a: "No. Your mobile information is never shared with third parties or affiliates for marketing or promotional purposes, and your opt-in consent data is never shared with any third parties. Full details are in our Privacy Policy.",
  },
  {
    q: "Do I need to download an app?",
    a: "Nope — that's the whole point. Everything happens in your regular text messages, the app you already check a hundred times a day. No downloads, no accounts, no passwords.",
  },
];

export const FaqSection = () => (
  <section
    id="faq"
    data-testid="faq-section"
    className="relative bg-[#FAF8F5] py-24 sm:py-36 grain"
  >
    <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-start">
      <div className="lg:sticky lg:top-32">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="font-mono2 text-xs tracking-[0.25em] uppercase text-[#4A7C59] mb-4"
        >
          FAQ
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-[#1E2022] leading-[1.08]"
          data-testid="faq-heading"
        >
          Good questions, <span className="italic text-[#D96B43]">honest answers</span>.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-5 text-base text-[#575B60] leading-relaxed max-w-md"
        >
          Everything carriers, reviewers, and real humans usually ask about our
          text reminders — in plain language.
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7 }}
      >
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="border-b border-[#1E2022]/10"
              data-testid={`faq-item-${i + 1}`}
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i + 1}`}
                className="py-6 text-left font-display font-bold text-lg sm:text-xl text-[#1E2022] hover:text-[#D96B43] hover:no-underline transition-colors duration-200 [&[data-state=open]]:text-[#D96B43]"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-content-${i + 1}`}
                className="text-sm sm:text-base text-[#575B60] leading-relaxed pr-8"
              >
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </motion.div>
    </div>
  </section>
);
